import { useEffect, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { CheckCircle2, Loader2, Download } from "lucide-react";

export default function OrderSuccess() {
  const loc = useLocation();
  const polled = useRef(0);
  const [tx, setTx] = useState(null);
  const [err, setErr] = useState("");
  const [productFile, setProductFile] = useState("");
  const params = new URLSearchParams(loc.search);
  const guestToken = params.get("t") || "";

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { setErr("Missing session_id"); return; }
    let alive = true;
    const tick = async () => {
      if (!alive) return;
      polled.current += 1;
      try {
        // Guest checkout attaches ?t=<token>; pass it through so the API accepts
        // us without a login session.
        const url = guestToken ? `/orders/status/${sid}?t=${guestToken}` : `/orders/status/${sid}`;
        const { data } = await api.get(url);
        setTx(data);
        if (data.payment_status === "paid") {
          try {
            const r = await api.get(`/products/${data.product_id}`);
            setProductFile(r.data?.file_url || "");
          } catch { /* ignore */ }
          return;
        }
        if (polled.current >= 8) return;
      } catch (e) { setErr(formatErr(e)); return; }
      setTimeout(tick, 2000);
    };
    tick();
    return () => { alive = false; };
  }, [loc.search, guestToken]);

  return (
    <div className="max-w-xl mx-auto px-6 py-20 text-center" data-testid="order-success-page">
      {!tx && !err && (<><Loader2 className="mx-auto animate-spin" size={32}/><h1 className="h-display text-3xl mt-6">Confirming your order…</h1></>)}
      {tx?.payment_status === "paid" && (
        <>
          <CheckCircle2 className="mx-auto text-[#0A0A0A]" size={48}/>
          <h1 className="h-display text-4xl mt-6">It&apos;s yours.</h1>
          <p className="text-[#4A4A4A] mt-3">{tx.product_title} · ${tx.amount?.toFixed(2)}</p>
          {tx.buyer_email && !tx.buyer_id && (
            <p className="text-xs text-[#0057FF] mt-2" data-testid="order-guest-note">
              We&apos;ve sent a copy of your receipt & download link to <b>{tx.buyer_email}</b>.
            </p>
          )}
          {productFile ? (
            <a href={productFile} target="_blank" rel="noreferrer" className="btn-primary mt-8 inline-flex" data-testid="order-download-btn">
              <Download size={16}/> Download
            </a>
          ) : (
            <p className="text-sm text-[#8A8A8A] mt-6">
              {tx.buyer_id ? "Your purchase is in your Orders." : "Your seller will ship this item and email you tracking details."}
            </p>
          )}
          <div className="mt-4">
            {tx.buyer_id ? (
              <Link to="/orders" className="btn-secondary inline-flex" data-testid="order-view-all">All my orders</Link>
            ) : guestToken ? (
              <Link to={`/order/access/${guestToken}`} className="btn-secondary inline-flex" data-testid="order-guest-view">
                Bookmark this receipt
              </Link>
            ) : null}
          </div>
        </>
      )}
      {tx && tx.payment_status !== "paid" && (<><Loader2 className="mx-auto animate-spin" size={32}/><h1 className="h-display text-3xl mt-6">Payment is processing…</h1></>)}
      {err && (<><h1 className="h-display text-3xl text-[#B84B4B]">Something went wrong</h1><p>{err}</p></>)}
    </div>
  );
}
